import { useState } from 'react';
import { useUserOpportunities, SenderStatus, Opportunity } from '@/hooks/useUserOpportunities';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RESPONSIVE, GRIDS, FORM_CONTROLS, SPACING, TYPOGRAPHY } from '@/design-system';
import { cn } from '@/lib/utils';
import { 
  Users, 
  Clock, 
  CheckCircle2, 
  X, 
  ArrowRight, 
  Sparkles,
  TrendingUp
} from 'lucide-react';
import { useLocale } from '@/contexts/LocaleContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LComponents, LCommon } from '@/components/i18n';
import { useLocalizations } from '@/hooks/useLocalizations';
import { useLocalizedRoutes } from '@/hooks/useLocalizedRoutes';
interface InquirySuggestionsProps {
  compact?: boolean;
  maxSuggestions?: number;
}

export function InquirySuggestions({ compact = false, maxSuggestions = 3 }: InquirySuggestionsProps) {
  const navigate = useNavigate();
  const { activeOpportunities, isLoading, updateSenderStatus } = useUserOpportunities();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<{ subject: string; status: SenderStatus } | null>(null);
  const { lComponents } = useLocalizations();
  const { formatRelativeTime } = useLocale();
  const { getPath } = useLocalizedRoutes();

  if (isLoading || !activeOpportunities || activeOpportunities.length === 0) {
    return null;
  }

  const visibleInquiries = activeOpportunities
    .filter((inquiry: Opportunity) => !dismissedIds.has(inquiry.id))
    .slice(0, maxSuggestions);

  if (visibleInquiries.length === 0) {
    return null;
  }

  const handleDismiss = (inquiryId: string) => {
    setDismissedIds(prev => new Set(prev).add(inquiryId));
  };

  const handleContactMore = () => {
    navigate(getPath('experts'));
  };

  const handleUpdateStatus = (subject: string, status: SenderStatus) => {
    setSelectedInquiry({ subject, status });
    setShowStatusDialog(true);
  };

  const confirmStatusUpdate = () => {
    if (selectedInquiry) {
      updateSenderStatus.mutate({ 
        subject: selectedInquiry.subject, 
        senderStatus: selectedInquiry.status 
      });
    }
    setShowStatusDialog(false);
  };

  // Group inquiries to count experts contacted
  const inquiryGroups = visibleInquiries.reduce((acc: Record<string, { inquiry: Opportunity; count: number; ids: string[] }>, inquiry: Opportunity) => {
    if (!acc[inquiry.subject]) {
      acc[inquiry.subject] = {
        inquiry,
        count: 0,
        ids: []
      };
    }
    acc[inquiry.subject].count++;
    acc[inquiry.subject].ids.push(inquiry.id);
    return acc;
  }, {} as Record<string, { inquiry: Opportunity; count: number; ids: string[] }>);

  const groupedInquiries = Object.values(inquiryGroups);

  if (compact && groupedInquiries.length === 1) {
    const group = groupedInquiries[0];
    const inquiry = group.inquiry;
    
    return (
      <Card className="border-primary/20 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
        <CardContent className={SPACING.cardContentCompact}>
          <div className={RESPONSIVE.headerWithActionsGap3}>
            <div className={cn(RESPONSIVE.flexRowCenterGap3, 'flex-1 min-w-0')}>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn(TYPOGRAPHY.cardTitleSmall, 'truncate')}>{inquiry.subject}</p>
                <p className={cn(TYPOGRAPHY.caption)}>
                  {lComponents('inquiry_suggestions.experts_contacted', { count: group.count, defaultValue: 'Experts Contacted' })} • {formatRelativeTime(inquiry.created_at)}
                </p>
              </div>
            </div>
            <div className={cn(RESPONSIVE.flexRowCenterGap2, 'flex-shrink-0')}>
              <Button size="sm" onClick={handleContactMore} className="gap-2">
                <ArrowRight className="h-4 w-4" />
                <LComponents
                  id="inquiry_suggestions.contact_more"
                  defaultValue='Connect with More Professionals' />
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => handleDismiss(inquiry.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-primary/20 bg-gradient-to-br from-background via-primary/5 to-background overflow-hidden">
        <CardContent className={SPACING.cardContentStandard}>
          {/* Header */}
          <div className={cn(SPACING.sectionHeaderLarge, 'flex items-start justify-between')}>
            <div className={cn(RESPONSIVE.flexRowCenterGap3)}>
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className={cn(RESPONSIVE.flexRowCenterGap2, TYPOGRAPHY.sectionTitle)}>
                  <LComponents id="inquiry_suggestions.title" defaultValue='Suggested Inquiries' />
                  <Badge variant="secondary" className="gap-1">
                    {groupedInquiries.length}
                  </Badge>
                </h3>
                <p className={cn(TYPOGRAPHY.cardDescription)}>
                  <LComponents
                    id="inquiry_suggestions.subtitle"
                    defaultValue='Frequent Inquiries Based on Your Profile' />
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => groupedInquiries.forEach(g => handleDismiss(g.inquiry.id))}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              <LComponents id="inquiry_suggestions.dismiss_all" defaultValue='Dismiss All' />
            </Button>
          </div>

          {/* Inquiries Grid */}
          <div className={cn(GRIDS.featuredGrid, SPACING.sectionHeaderLarge)}>
            {groupedInquiries.map((group) => {
              const inquiry = group.inquiry;
              return (
                <Card key={inquiry.subject} className="border-border/50 hover:border-primary/50 transition-colors">
                  <CardContent className={SPACING.cardContentCompact}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h4 className={cn(TYPOGRAPHY.cardTitleSmall, 'mb-1 truncate')}>{inquiry.subject}</h4>
                        <p className={cn(TYPOGRAPHY.caption, 'line-clamp-2 mb-2')}>
                          {inquiry.message}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(FORM_CONTROLS.button.iconTouchXs, "flex-shrink-0 ml-2")}
                        onClick={() => handleDismiss(inquiry.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Metadata */}
                    <div className={cn(SPACING.listItemGap, 'mb-3')}>
                      <div className={cn(RESPONSIVE.flexRowCenterGap2, 'text-xs')}>
                        <Users className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium">{group.count}</span>
                        <span className="text-muted-foreground">
                          <LComponents id="inquiry_suggestions.contacted" defaultValue='Contacted' />
                        </span>
                      </div>
                      <div className={cn(TYPOGRAPHY.caption, RESPONSIVE.flexRowCenterGap2)}>
                        <Clock className="h-3 w-3" />
                        {formatRelativeTime(inquiry.created_at)}
                      </div>
                      {inquiry.service_type && (
                        <Badge variant="outline" className="text-xs">
                          {inquiry.service_type}
                        </Badge>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleContactMore}
                        className="flex-1 h-8 text-xs gap-1"
                      >
                        <ArrowRight className="h-3 w-3" />
                        <LComponents id="inquiry_suggestions.more_experts" defaultValue='Find More Experts' />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateStatus(inquiry.subject, 'resolved')}
                        className="h-8 px-2"
                      >
                        <CheckCircle2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className={`${RESPONSIVE.headerWithActionsGap3} p-4 rounded-lg bg-primary/5 border border-primary/10`}>
            <div className={cn(RESPONSIVE.flexRowCenterGap3)}>
              <TrendingUp className="h-5 w-5 text-primary" />
              <div>
                <p className={cn(TYPOGRAPHY.cardTitleSmall)}>
                  <LComponents id="inquiry_suggestions.cta.title" defaultValue='Request Expert Advice' />
                </p>
              <p className={cn(TYPOGRAPHY.caption)}>
                  <LComponents
                    id="inquiry_suggestions.cta.subtitle"
                    defaultValue='Browse more experts.' />
                </p>
              </div>
            </div>
            <Button onClick={handleContactMore} className="gap-2">
              <LComponents
                id="inquiry_suggestions.browse_experts"
                defaultValue='Browse Tax Professionals' />
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
      <AlertDialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <LComponents
                id="inquiry_suggestions.resolve_dialog.title"
                defaultValue='Confirm Resolution' />
            </AlertDialogTitle>
            <AlertDialogDescription>
              {lComponents('inquiry_suggestions.resolve_dialog.description', { subject: selectedInquiry?.subject || '', defaultValue: 'Confirming this will mark the inquiry as resolved and notify relevant parties.' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={RESPONSIVE.alertDialogFooter}>
            <AlertDialogCancel className="w-full sm:w-auto">
              <LCommon id="actions.cancel" defaultValue='Cancel' />
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusUpdate} className="w-full sm:w-auto">
              <LCommon id="actions.confirm" defaultValue='Confirm Selection' />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
